'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { CircleHelp } from 'lucide-react';

import { cn } from "@/lib/utils";

/** Shared TooltipContent `className` for investigation metrics and HelpHint hover (body typography). */
const investigationTooltipContentClassName =
  'max-w-[min(260px,calc(100vw-32px))] px-2.5 py-2 text-[11px] leading-snug font-normal font-sans';

/** TooltipPrimitive.Root — use under a shared TooltipProvider when grouping many tooltips. */
const TooltipRoot = TooltipPrimitive.Root;

/**
 * Label + trailing CircleHelp for dense copy. Forwards Radix Trigger props (required when used with TooltipTrigger asChild).
 */
const MetricTooltipTrigger = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { children: React.ReactNode }
>(function MetricTooltipTrigger({ children, className, tabIndex, ...props }, ref) {
  return (
    <span
      ref={ref}
      {...props}
      tabIndex={tabIndex ?? 0}
      className={cn(
        'inline-flex max-w-full cursor-help items-center gap-0.5 rounded-sm transition-opacity outline-none hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--color-surface-secondary)]',
        className,
      )}
    >
      {children}
      <CircleHelp className="pointer-events-none size-2.5 shrink-0 opacity-45" strokeWidth={2.5} aria-hidden />
    </span>
  );
});
MetricTooltipTrigger.displayName = 'MetricTooltipTrigger';

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[60] w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md border border-[var(--tooltip-border)] bg-[var(--tooltip-surface)] px-3 py-1.5 text-xs text-balance text-[var(--tooltip-foreground)] shadow-[var(--tooltip-shadow)]',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-[60] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] fill-[var(--tooltip-surface)]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export {
  Tooltip,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  MetricTooltipTrigger,
  investigationTooltipContentClassName,
};
